import { Intent } from '@blueprintjs/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '../../lib/queryKeys';
import { Notifications } from '../../notifications/components/Notifications';
import type { RequestError } from '../../shared';
import { observableToPromise } from '../../shared/observableToPromise';
import { type MemberResponse, putAccountOwner } from '../api';
import type { Account, OwnerPromotion } from '../model';

/**
 * Promotes a new account owner via react-query (no Redux).
 *
 * Replaces the `ownerUpdateRequest` Redux slice and the effect in
 * AccountOwnerPanelContainer that watched `isFetching` for a true -> false
 * transition to decide the update had succeeded. That pattern inferred success
 * from the absence of an error rather than from an actual success signal;
 * `onSuccess` here only runs when the request genuinely succeeded.
 *
 * The endpoint returns the re-normalized member collection, but roles are
 * derived server-side across the whole account, so the members queries are
 * invalidated rather than written directly.
 */
// `account` is required rather than nullable: the only caller,
// AccountOwnerPanelContainer, takes a non-null Account prop. An internal
// null-guard would have to throw something to satisfy the TError generic below,
// and there is no honest value to throw for a case the types already exclude.
export function useUpdateAccountOwner(account: Account) {
  const queryClient = useQueryClient();

  // Typed error generic so callers get RequestError | null without a cast. Every
  // error therefore originates from putAccountOwner, which rejects with one.
  return useMutation<MemberResponse, RequestError, OwnerPromotion>({
    mutationFn: async (promotion: OwnerPromotion) => observableToPromise(putAccountOwner(account, promotion)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accounts.allMembers(account._id),
      });

      // Previously emitted by the notifications epic reacting to
      // UPDATE_ACCOUNT_OWNER_FULFILLED.
      Notifications.show({
        intent: Intent.SUCCESS,
        message: 'Updated the account owner successfully',
      });
    },
    onError: (error) => {
      const raw = error?.message;
      const message = (typeof raw === 'object' ? (raw as { [key: string]: string }).message : raw) || 'Unknown error';
      Notifications.show({
        intent: Intent.DANGER,
        message: `Failed to update account owner: ${message}`,
      });
    },
  });
}
